/* prettier-ignore */

import { Injectable, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NewCustomRouteService } from 'src/app/pages/new-custom-route/new-custom-route.service';
import dayjs, { Dayjs } from 'dayjs';
import { DebugService } from 'src/app/services/debug.service';

@Injectable({ providedIn: 'root' })

export class RegisterFormService {
    private _customRouteService = inject(NewCustomRouteService);
    private debugService = inject(DebugService);
    private _fb = inject(FormBuilder);
    private form: FormGroup<any> = this._fb.group<any>({
        startDate:            new FormControl<Date | null>(null, Validators.required),
        endDate:              new FormControl<Date | null>(null, Validators.required),
        startTime:            new FormControl<string | null>(null),
        endTime:              new FormControl<string | null>(null),
        startLocationAddress: new FormControl<string>(null),
        startLocationLat:     new FormControl<number>(null),
        startLocationLng:     new FormControl<number>(null),
        tripType:             new FormControl<any>(null, Validators.required),
        interestIdList:       new FormControl<number[]>(null,[Validators.required]),
    });

    private loadStorageData = effect(() => {
        const loadStorage = this._customRouteService.loadStorageData();
        if (loadStorage){
            this.debugService.info('Loading Resgister Data From LocalStorage', '[CICERONE]');
            this._customRouteService.loadStorageData.set(false);
            this.setDataFromLocalStorage();
            this._customRouteService.filterAppItems();
        }
    }, { allowSignalWrites: true })

    public readonly signalForm = toSignal(
        this.form.valueChanges,
        { initialValue: this.form.getRawValue() }
    );

    public getFormValues(){
        return this.form.value as unknown as any
    }

    public isValidForm(){
        return this.form.valid;
    }

    public setDateRange(date): void {
        this.debugService.info(date, '[CHATBOT ACTIONS] Setting DateRange');
        this.setLocalStorageData('startDate', date.start_date);
        this.setLocalStorageData('endDate', date.end_date);
        const startDate =  dayjs(date.start_date, 'DD/MM/YYYY');
        const endDate =  dayjs(date.end_date, 'DD/MM/YYYY');

        this._customRouteService.routeConfig = { 
            ...this._customRouteService.routeConfig, 
            startDate,
            endDate
        };

        this.form.patchValue({ 
            startDate: date.start_date, 
            endDate: date.end_date 
        });
    }

    public setTripType(data) {
        this.debugService.info(data, '[CHATBOT ACTIONS] Setting Trip Type');
        if (!data)
            return { 
                is_successful: false, 
                reason: 'Se deben de especificar el tipo de viajero'
            }

        const group_id = Number(data);
        const travelerTypesApiData = this._customRouteService.getTravelerType()?.map(e => e.id);
        if (!travelerTypesApiData.includes(group_id))
            return { 
                is_successful: false, 
                reason: 'Tipo de viajero incorrecto o no perteneciente a cliente'
            }

        this._customRouteService.routeConfig = { 
            ...this._customRouteService.routeConfig, 
            travellerType: group_id?.toString()
        };

        this.setLocalStorageData('group_id', group_id);
        this.form.get('tripType').setValue(group_id);
        return { is_successful: true }
    }

    public setInterestIdList(data) {
        this.debugService.info(data, '[CHATBOT ACTIONS] Setting Interest List');
        if (!data || data?.length === 0)
            return { 
                is_successful: false, 
                reason: 'Se deben de seleccionar al menos una categoría'
            }

        const interest_ids = data?.map(e => Number(e));
        const categoriesApiData = this._customRouteService.getCategories()?.map(e => e.id);
        if (!interest_ids.every(categoryId => categoriesApiData?.includes(categoryId)))
            return { 
                is_successful: false, 
                reason: 'Categorías incorrectas o no perteneciente a cliente'
            }

        const categoriesIds = interest_ids?.map(id => `${id}`);
        const categories = categoriesIds?.map(id => ({id: parseInt(id), subcategories: []}));
        this._customRouteService.routeConfig = { 
            ...this._customRouteService.routeConfig, 
            categories
        };

        this.setLocalStorageData('interest_ids', categoriesIds);
        this.form.get('interestIdList').setValue(interest_ids);
        return { is_successful: true }
    }
    
    public setStartLocation(address){
        const { latitude, longitude, title } = address;
        this.form.get('startLocationAddress').setValue(title);
        this.form.get('startLocationLat').setValue(latitude);
        this.form.get('startLocationLng').setValue(longitude);
        return { is_successful: true }
    }

    public getInvalidControls(): { controlName: string; errors: ValidationErrors }[] {
        const invalidEntries: { controlName: string; errors: ValidationErrors }[] = [];
        Object.keys(this.form.controls).forEach(key => {
            const control = this.form.get(key);
            if (control && control.invalid) {
                invalidEntries.push({
                    controlName: key,
                    errors: control.errors!
                });
            }
        });
        return invalidEntries;
    }

    public resetForm(): void {
        this.form.reset({
            startDate: null,
            endDate: null,
            startTime: null,
            endTime: null,
            startLocationAddress: null,
            startLocationLat: null,
            startLocationLng: null,
            tripType: null,
            interestIdList: [],
        })
    }

        setLocalStorageData(key, item){
        localStorage.setItem(key, JSON.stringify(item));
    }

    getLocalStorageData(key){
        try { return JSON.parse(localStorage.getItem(key))}
        catch(e) { return null }
    }

    resetLocalStorage(){
        localStorage.removeItem('customRoute');
        localStorage.removeItem('startDate');
        localStorage.removeItem('endDate');
        localStorage.removeItem('group_id');
        localStorage.removeItem('interest_ids');
    }

    setDataFromLocalStorage(){
        const startDate = this.getLocalStorageData('startDate');
        const endDate = this.getLocalStorageData('endDate');
        if (startDate && endDate){
            const date = { start_date: startDate, end_date: endDate };
            this.setDateRange(date);
        }

        const group_id = this.getLocalStorageData('group_id');
        if (group_id)
            this.setTripType(group_id);

        const interest_ids = this.getLocalStorageData('interest_ids');
        if (interest_ids)
            this.setInterestIdList(interest_ids);
    }
}